import { useCallback, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";

import { getDownloadUrl } from "../api/files.api";
import { folderChildrenQueryKey } from "./useFolderChildren";
import type { FileListItem } from "../types/project-storage.types";
import { mapStorageError } from "../utils/storageErrors";

type CachedDownload = {
  downloadUrl: string;
  fetchedAt: number;
  expiresIn: number;
};

const EXPIRY_SAFETY_RATIO = 0.9;

async function downloadFromUrl(url: string, displayName: string): Promise<void> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Download failed with status ${response.status}`);
  }

  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);

  try {
    const anchor = document.createElement("a");
    anchor.href = blobUrl;
    anchor.download = displayName;
    anchor.rel = "noopener";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}

export function useFileDownload(mmCode: number | null, folderId: string | null) {
  const queryClient = useQueryClient();
  const cacheRef = useRef<Map<string, CachedDownload>>(new Map());
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);

  const invalidateChildren = useCallback(() => {
    if (mmCode !== null && folderId) {
      queryClient.invalidateQueries(folderChildrenQueryKey(mmCode, folderId));
    }
  }, [folderId, mmCode, queryClient]);

  const downloadFile = useCallback(
    async (file: FileListItem) => {
      if (mmCode === null) return;

      setDownloadingFileId(file.id);

      try {
        const cached = cacheRef.current.get(file.id);
        const now = Date.now();

        if (
          cached &&
          now - cached.fetchedAt < cached.expiresIn * 1000 * EXPIRY_SAFETY_RATIO
        ) {
          await downloadFromUrl(cached.downloadUrl, file.displayName);
          return;
        }

        const { downloadUrl, expiresIn } = await getDownloadUrl(mmCode, file.id);
        cacheRef.current.set(file.id, {
          downloadUrl,
          fetchedAt: now,
          expiresIn,
        });
        await downloadFromUrl(downloadUrl, file.displayName);
      } catch (error: unknown) {
        const mapped = mapStorageError(error, "download");

        if (mapped.errorCode === "FILE_NOT_FOUND") {
          cacheRef.current.delete(file.id);
          toast.error(mapped.message);
          invalidateChildren();
          return;
        }

        toast.error(mapped.message);
      } finally {
        setDownloadingFileId(null);
      }
    },
    [invalidateChildren, mmCode]
  );

  return {
    downloadingFileId,
    downloadFile,
  };
}
